import { useRef, ReactElement } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import CircularProgress from '@mui/material/CircularProgress';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CssBaseline from '@mui/material/CssBaseline';
import Paper from '@mui/material/Paper';
import SaveIcon from '@mui/icons-material/Save';
import { styled } from '@mui/material/styles';
import { BEM_NAME_ELEMENTS, NAVIGATION_ACTIONS } from '../_const';
import { DRAWER_WIDTH } from 'pages/ApplicationRequest/components/containers/1616BridgesEpic/components/HelpBar';

/**
 * Bottom-anchored form navigation with additional styles
 */
const StyledBottomNavigation = styled(BottomNavigation, {
	shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
	transition: theme.transitions.create(['margin', 'width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		width: `calc(100% - ${DRAWER_WIDTH}px)`,
		transition: theme.transitions.create(['margin', 'width'], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginRight: DRAWER_WIDTH,
	}),
}));

/**
 * Step navigation buttons
 * @param {Function} props.onChange button click handler
 * @param {number} props.currentStep current step index
 * @param {number} props.stepCount total steps expected
 * @param {boolean} props.hasErrors errors to prevent submission
 * @param {boolean} props.isSubmitting indicates form is attempting submission
 * @param {boolean} props.relativeNav anchor component at bottom of container or bottom of page
 * @param {boolean} props.alternativeSubmission subverts the expected form
 * @param {boolean} props.open indicates if help panel is open
 * @returns {ReactElement} buttons to traverse the step-enabled form
 */
const FixedBottomNavigation = ({
	onChange,
	currentStep,
	stepCount,
	hasErrors,
	isSubmitting = false,
	isSubmitted = false,
	relativeNav = false,
	open = false,
	keepSubmitDisabled = false,
	keepNextDisabled = false,
	keepBackDisabled = false,
}) => {
	const ref = useRef(null);
	const isBackDisabled = currentStep === 0 || isSubmitted || keepBackDisabled;
	const isNextDisabled = currentStep === stepCount - 1 || keepNextDisabled;
	const isSubmitDisabled =
		hasErrors || isSubmitting || isSubmitted || keepSubmitDisabled;

	return (
		<Box sx={{ pb: 7 }} ref={ref}>
			<CssBaseline />
			<Paper
				sx={{
					position: relativeNav ? 'absolute' : 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 2,
				}}
				elevation={3}
			>
				<StyledBottomNavigation
					showLabels
					onChange={(_, newValue) => {
						onChange(newValue);
					}}
					sx={{
						marginRight: open ? `${DRAWER_WIDTH / 2}px` : 0,
					}}
				>
					<BottomNavigationAction
						id={`${BEM_NAME_ELEMENTS.VIEW_NAVIGATION}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--prev`}
						label={
							<Typography variant='subtitle2'>Back</Typography>
						}
						icon={<NavigateBeforeIcon />}
						value={NAVIGATION_ACTIONS.PREVIOUS}
						disabled={isBackDisabled}
						disableRipple={isBackDisabled}
						sx={{
							color: theme =>
								isBackDisabled
									? theme.palette.grayscale.main
									: theme.palette.primary.dark,
						}}
					/>

					<BottomNavigationAction
						id={`${BEM_NAME_ELEMENTS.VIEW_NAVIGATION}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--next`}
						label={
							<Typography variant='subtitle2'>Next</Typography>
						}
						icon={<NavigateNextIcon />}
						value={NAVIGATION_ACTIONS.NEXT}
						disabled={isNextDisabled}
						disableRipple={isNextDisabled}
						sx={{
							color: theme =>
								isNextDisabled
									? theme.palette.grayscale.main
									: theme.palette.primary.dark,
						}}
					/>
					<BottomNavigationAction
						id={`${BEM_NAME_ELEMENTS.VIEW_NAVIGATION}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--submit`}
						label={
							<Typography variant='subtitle2'>Submit</Typography>
						}
						icon={
							isSubmitting ? (
								<CircularProgress size={20} />
							) : (
								<SaveIcon />
							)
						}
						value={NAVIGATION_ACTIONS.SUBMIT}
						type='submit'
						disabled={isSubmitDisabled}
						disableRipple={isSubmitDisabled}
						sx={{
							color: theme =>
								isSubmitDisabled
									? theme.palette.grayscale.main
									: theme.palette.primary.dark,
						}}
					/>
				</StyledBottomNavigation>
			</Paper>
		</Box>
	);
};

FixedBottomNavigation.propTypes = {
	onChange: PropTypes.func.isRequired,
	currentStep: PropTypes.number.isRequired,
	stepCount: PropTypes.number.isRequired,
	hasErrors: PropTypes.bool.isRequired,
	isSubmitting: PropTypes.bool,
	isSubmitted: PropTypes.bool,
	relativeNav: PropTypes.bool,
	open: PropTypes.bool,
};

export default FixedBottomNavigation;
